import { App } from 'antd';
import type { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '@/services/api/client';
import * as reportsApi from '@/services/api/reports';
import * as usersApi from '@/services/api/users';
import { useAuthStore } from '@/stores/auth.store';
import type { ReportRecord } from '@/types/report';
import type { UserRecord } from '@/types/user';
import {
  buildSubordinateReportRows,
  countTeamSubmission,
  type SubordinateReportRow,
} from '@/utils/leader-team';

export function useLeaderTeamSnapshot(reportDate: Dayjs) {
  const { message } = App.useApp();
  const currentUser = useAuthStore((state) => state.user);
  const dateKey = reportDate.format('YYYY-MM-DD');

  const [loading, setLoading] = useState(false);
  const [subordinates, setSubordinates] = useState<UserRecord[]>([]);
  const [reports, setReports] = useState<ReportRecord[]>([]);

  const load = useCallback(async () => {
    if (!currentUser) {
      return;
    }

    setLoading(true);
    try {
      const subs = await usersApi.fetchDirectSubordinates();
      setSubordinates(subs);

      if (subs.length === 0) {
        setReports([]);
        return;
      }

      const result = await reportsApi.fetchReports({
        page: 1,
        pageSize: 100,
        startDate: dateKey,
        endDate: dateKey,
      });
      setReports(result.items);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载团队数据失败';
      message.error(text);
      setSubordinates([]);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser, dateKey, message]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows: SubordinateReportRow[] = useMemo(
    () => buildSubordinateReportRows(subordinates, reports),
    [subordinates, reports],
  );

  const stats = useMemo(() => countTeamSubmission(rows), [rows]);

  return {
    loading,
    subordinates,
    reports,
    rows,
    stats,
    dateKey,
    reload: load,
  };
}
